import { Spinner } from '@/components/glitch-ui/spinner';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Users, Swords, Linkedin, Facebook, Instagram, Megaphone, Eye, FilePlus, FolderPlus, AlertCircle, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { getCampaignShortName } from '@/lib/utils';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTableSorting } from '@/hooks/useTableSorting';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { SortableHeader } from '@/components/glitch-ui/data-table/sortable-header';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';

import { generateCompetitorReport, deleteCompetitor, startCompetitorGeneration, updateCompetitor } from '@/lib/apis';
import { useAtomValue, useSetAtom } from 'jotai';
import { organizationDataAtom, googleAdsAccountAtom, campaignCreationPreSelectionAtom } from '@/store';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { CAMPAIGN_TYPE } from '@/lib/types';
import { AddCompetitorModal } from '@/components/competitors/add-competitor-modal';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { competitorTasksQueryOptions, useReportsQuery, reportTasksQueryOptions, businessQueryOptions, competitorsByBusinessQueryOptions } from '@/lib/query-options';
import { usePaymentGate } from '@/hooks/usePaymentGate';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import { withOrgReset } from '@/components/glitch-ui/with-org-reset';

export const Route = createFileRoute('/_authenticated/_layout/competitors/')({
  component: withOrgReset(CompetitorsPage),
  staticData: {
    title: 'Competitors',
    icon: Swords,
  },
});

// Helper to get competitor ID from URL path
function getCompetitorIdFromUrl(): string | null {
  const urlPath = window.location.pathname;
  const pathParts = urlPath.split('/');
  const lastPart = pathParts[pathParts.length - 1];

  // If last part is 'competitors' or empty, no competitor selected
  if (!lastPart || lastPart === 'competitors') {
    return null;
  }

  // Otherwise it's a competitor ID (UUID format)
  return lastPart;
}

// Helper function to get similarity label and colors based on closeness score
function getCompetitorRelationship(closeness: number): { label: string; bgColor: string; textColor: string } {
  if (closeness >= 1.0) {
    return { label: 'Sub-brand', bgColor: 'bg-green-100', textColor: 'text-green-600' };
  } else if (closeness >= 0.8) {
    return { label: 'Direct', bgColor: 'bg-orange-50', textColor: 'text-orange-600' };
  } else if (closeness >= 0.5) {
    return { label: 'Adjacent', bgColor: 'bg-purple-50', textColor: 'text-purple-600' };
  } else if (closeness >= 0.2) {
    return { label: 'Complementary', bgColor: 'bg-indigo-50', textColor: 'text-indigo-600' };
  } else {
    return { label: 'Unrelated', bgColor: 'bg-gray-100', textColor: 'text-gray-600' };
  }
}

// #4792: the 5 editable Proximity relationships and their representative closeness scores. The
// auto-assigned closeness is a 0–1 float; editing snaps it to one of these representative values.
const PROXIMITY_RELATIONSHIPS = [
  { label: 'Sub-brand', score: 1.0 },
  { label: 'Direct', score: 0.8 },
  { label: 'Adjacent', score: 0.5 },
  { label: 'Complementary', score: 0.2 },
  { label: 'Unrelated', score: 0.0 },
] as const;
const PROXIMITY_SCORE_BY_LABEL: Record<string, number> = Object.fromEntries(
  PROXIMITY_RELATIONSHIPS.map((r) => [r.label, r.score]),
);

// Editable Proximity control: a badge-styled Select of the 5 relationships, preserving the existing
// color mapping. stopPropagation so opening it doesn't also select the competitor row.
function ProximitySelect({
  closeness,
  onChange,
  disabled,
}: {
  closeness: number;
  onChange: (score: number) => void;
  disabled?: boolean;
}) {
  const relationship = getCompetitorRelationship(closeness);
  return (
    <Select
      value={relationship.label}
      onValueChange={(label) => {
        const score = PROXIMITY_SCORE_BY_LABEL[label];
        if (score !== undefined && score !== closeness) onChange(score);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        onClick={(e) => e.stopPropagation()}
        className={`h-auto w-fit rounded-full border-0 px-2.5 py-1 text-xs font-medium focus:ring-1 focus:ring-[#290BD9] [&>svg]:static [&>svg]:ml-1 [&>svg]:size-3 [&>svg]:opacity-60 ${relationship.bgColor} ${relationship.textColor}`}
      >
        {relationship.label}
      </SelectTrigger>
      <SelectContent>
        {PROXIMITY_RELATIONSHIPS.map(({ label }) => {
          const r = getCompetitorRelationship(PROXIMITY_SCORE_BY_LABEL[label]);
          return (
            <SelectItem key={label} value={label} className="text-xs">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${r.bgColor} ${r.textColor}`}>
                {label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

interface CompetitorAdInfo {
  id: number;
  slug: string;
}

interface CompetitorCampaignInfo {
  id: number;
  slug: string;
  name: string;
  ad: CompetitorAdInfo | null;
}

interface Competitor {
  id: string;
  business_id: number;
  competitor_brand_name: string;
  primary_website: string | null;
  social_media: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
    youtube?: string;
  };
  competitor_closeness: number;
  description: string | null;
  location: {
    town?: string;
    region?: string;
    country?: string;
  };
  notes: string | null;
  competitor_metadata: {
    industry?: string;
    audience?: string;
    founders?: string[];
    founding_year?: number;
    company_size?: string;
    headquarters?: string;
  };
  created_at: string;
  updated_at: string;
  is_loading: boolean;
  campaign: CompetitorCampaignInfo | null;
}

export function CompetitorsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { toast } = useToast();
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const setPreSelection = useSetAtom(campaignCreationPreSelectionAtom);
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<string | null>(() => getCompetitorIdFromUrl());

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [generatingReportIds, setGeneratingReportIds] = useState<Set<string>>(new Set());
  const [competitorToDelete, setCompetitorToDelete] = useState<Competitor | null>(null);
  // Fetch business data for the current organization (1:1 relationship)
  const { data: business, isLoading: isBusinessLoading } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Competitors via the canonical query options (#4907) — the previous
  // useEffect+setState mirror bypassed cache/dedup/invalidation and drifted.
  const {
    data: serverCompetitors = [],
    isLoading,
    error: competitorsError,
  } = useQuery({
    ...competitorsByBusinessQueryOptions(business?.id ?? 0),
    select: (data): Competitor[] => data as Competitor[],
  });
  const error = competitorsError ? 'Failed to load competitors' : null;
  // Only show the full-page loader once loading outlasts a short delay — a fast/cached navigation
  // (the common case) resolves before then and never flashes the spinner (#5342 polish).
  const isInitialLoading = isBusinessLoading || isLoading;
  const showLoader = useDelayedFlag(isInitialLoading, 200);

  // #4792: proximity edits stage locally, keyed by competitor id, and persist only on "Save Changes".
  // The competitor list stays owned by React Query (#4907); staged edits are overlaid at render time
  // rather than mutating the cached array, so the query remains the single source of truth.
  const [stagedCloseness, setStagedCloseness] = useState<Record<string, number>>({});
  const [isSavingProximity, setIsSavingProximity] = useState(false);
  const competitors = useMemo(
    () =>
      serverCompetitors.map((c) =>
        c.id in stagedCloseness ? { ...c, competitor_closeness: stagedCloseness[c.id] } : c,
      ),
    [serverCompetitors, stagedCloseness],
  );

  // #4792: stage a competitor's Proximity (closeness) edit. Compared against the SERVER value so
  // re-selecting the original relationship clears the staged edit and the page goes clean again.
  const handleProximityChange = (competitor: Competitor, score: number) => {
    const serverValue = serverCompetitors.find((c) => c.id === competitor.id)?.competitor_closeness;
    setStagedCloseness((prev) => {
      const next = { ...prev };
      if (serverValue === undefined || score === serverValue) {
        delete next[competitor.id];
      } else {
        next[competitor.id] = score;
      }
      return next;
    });
  };

  // Dirty when any competitor has a staged proximity edit.
  const isDirty = Object.keys(stagedCloseness).length > 0;

  // #4792: persist every staged proximity change, then invalidate so the query refetches the
  // canonical values and the overlay clears.
  const saveProximityChanges = async () => {
    const changed = Object.entries(stagedCloseness);
    if (changed.length === 0) return;
    setIsSavingProximity(true);
    try {
      await Promise.all(
        changed.map(([id, closeness]) => updateCompetitor(id, { competitor_closeness: closeness })),
      );
      changed.forEach(([id, closeness]) => {
        posthog?.capture('competitor_proximity_edited', { competitor_id: id, closeness });
      });
      setStagedCloseness({});
      if (business?.id) {
        queryClient.invalidateQueries({ queryKey: ['competitors', business.id] });
      }
    } catch {
      toast({ title: 'Could not save proximity changes', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setIsSavingProximity(false);
    }
  };

  // Revert every staged proximity edit back to the last-saved server values.
  const discardProximityChanges = () => setStagedCloseness({});

  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty,
    onSave: saveProximityChanges,
    onDiscard: discardProximityChanges,
    isSaving: isSavingProximity,
    allowedBasePath: '/competitors',
    contextName: 'Competitors',
  });

  const hasTrackedPageView = useRef(false);

  const generateMutation = useMutation({
    mutationFn: () => startCompetitorGeneration(business!.business_url, 10, business!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitor-tasks', business?.id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (competitorId: string) => deleteCompetitor(competitorId),
    onSuccess: () => {
      posthog?.capture('competitor_deleted', { competitor_id: competitorToDelete?.id });
      queryClient.invalidateQueries({ queryKey: ['competitors', business?.id] });
      // Clear selection if deleted competitor was selected
      if (selectedCompetitorId === competitorToDelete?.id) {
        setSelectedCompetitorId(null);
        navigate({ to: '/competitors', replace: true });
      }
      setCompetitorToDelete(null);
    },
  });

  // Track competitors page viewed - only once per mount
  useEffect(() => {
    if (!hasTrackedPageView.current && !isLoading) {
      hasTrackedPageView.current = true;
      posthog?.capture('competitors_page_viewed', {
        organization_id: organizationData?.id,
        competitor_count: competitors.length,
      });
    }
  }, [isLoading, competitors.length, posthog, organizationData?.id]);

  // Update URL when competitor selection changes
  const selectCompetitor = (competitorId: string | null) => {
    setSelectedCompetitorId(competitorId);

    // Track competitor selection
    if (competitorId) {
      const competitor = competitors.find(c => c.id === competitorId);
      posthog?.capture('competitor_selected', {
        competitor_id: competitorId,
        competitor_name: competitor?.competitor_brand_name,
        competitor_url: competitor?.primary_website,
        organization_id: organizationData?.id,
      });
    }

    // Update URL using router navigation with replace to avoid polluting browser history
    if (competitorId) {
      navigate({ to: '/competitors/$competitorId', params: { competitorId }, replace: true });
    } else {
      navigate({ to: '/competitors', replace: true });
    }
  };

  // Payment gate for competitor report generation
  const { PaymentGate, requirePayment } = usePaymentGate({ source: 'competitor_report' });

  // Fetch competitor reports to show in the table
  const { data: competitorReports = [] } = useReportsQuery(
    organizationData?.id,
    1,
    100,
    'COMPETITOR_REPORT'
  );

  // Helper to get the most recent report for a competitor
  const getCompetitorReport = (competitorId: string) => {
    const reports = competitorReports.filter(
      (r) => r.report_metadata?.competitor_id === competitorId
    );
    // Reports are returned sorted by created_at desc, so first is most recent
    return reports[0] || null;
  };



  // 2. Poll task tracking status by business_id
  // This polls /competitors/business/{businessId}/tasks every 3 seconds while there are in-progress tasks
  const { data: taskStatus } = useQuery({
    ...competitorTasksQueryOptions(business?.id ?? 0),
    enabled: !!business?.id,
  });

  // 2b. Poll report task tracking status for "Generating" state in Report column
  const { data: reportTaskStatus, refetch: refetchReportTasks } = useQuery({
    ...reportTasksQueryOptions(business?.id ?? 0),
    enabled: !!business?.id,
  });

  // Helper to check if a competitor has a report being generated (match by URL)
  const isReportGenerating = (competitor: Competitor) => {
    if (!reportTaskStatus?.has_in_progress) return false;
    return reportTaskStatus.tasks.some(
      (task) => task.state === 'IN_PROGRESS' && task.url === competitor.primary_website
    );
  };

  // Helper to check if a competitor has a FAILED report task (no successful report yet)
  const hasFailedReportTask = (competitor: Competitor) => {
    if (!reportTaskStatus?.tasks) return false;
    const report = getCompetitorReport(competitor.id);
    if (report) return false; // Has successful report, ignore old failures
    // Don't show failed state in UI - just show normal "Generate" button
    return false;
  };

  // Track previous has_in_progress to detect when all tasks complete
  const [prevHasInProgress, setPrevHasInProgress] = useState<boolean | null>(null);
  const [prevReportHasInProgress, setPrevReportHasInProgress] = useState<boolean | null>(null);

  // 3. When all competitor tasks complete (has_in_progress goes from true to false), refetch competitors
  useEffect(() => {
    if (taskStatus) {
      const currentHasInProgress = taskStatus.has_in_progress;

      // If we were polling and now there are no in-progress tasks, refetch competitors
      if (prevHasInProgress === true && currentHasInProgress === false) {
        // Invalidate notifications so any new notifications appear
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
        if (business?.id) {
          queryClient.invalidateQueries({ queryKey: ['competitors', business.id] });
        }
      }

      setPrevHasInProgress(currentHasInProgress);
    }
  }, [taskStatus?.has_in_progress, business?.id, prevHasInProgress, queryClient]);

  // 3b. When all report tasks complete, refetch reports to update the Report column
  useEffect(() => {
    if (reportTaskStatus) {
      const currentHasInProgress = reportTaskStatus.has_in_progress;

      if (prevReportHasInProgress === true && currentHasInProgress === false) {
        // Invalidate reports query so "View" link appears
        // Use refetchType: 'active' and await it to ensure data is fetched before clearing generating state
        queryClient.invalidateQueries({ queryKey: ['reports'] }).then(() => {
          // Only reset local generating state AFTER reports have been refetched
          // This prevents the race condition where button shows "Generate" briefly
          setGeneratingReportIds(new Set());
        });
        // Invalidate notifications so any new notifications appear
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
      }

      setPrevReportHasInProgress(currentHasInProgress);
    }
  }, [reportTaskStatus?.has_in_progress, prevReportHasInProgress, queryClient]);

  // Sort competitors: loading rows go to end, custom sort for campaign (nulls last)
  const competitorSortFns = useMemo(() => ({
    competitor_brand_name: (a: Competitor, b: Competitor, dir: 'asc' | 'desc') => {
      if (a.is_loading !== b.is_loading) return a.is_loading ? 1 : -1;
      const cmp = a.competitor_brand_name.localeCompare(b.competitor_brand_name);
      return dir === 'asc' ? cmp : -cmp;
    },
    competitor_closeness: (a: Competitor, b: Competitor, dir: 'asc' | 'desc') => {
      if (a.is_loading !== b.is_loading) return a.is_loading ? 1 : -1;
      const cmp = a.competitor_closeness - b.competitor_closeness;
      return dir === 'asc' ? cmp : -cmp;
    },
    campaign: (a: Competitor, b: Competitor, dir: 'asc' | 'desc') => {
      if (a.is_loading !== b.is_loading) return a.is_loading ? 1 : -1;
      const aName = a.campaign?.name || '';
      const bName = b.campaign?.name || '';
      if (!aName && !bName) return 0;
      if (!aName) return 1;
      if (!bName) return -1;
      const cmp = aName.localeCompare(bName);
      return dir === 'asc' ? cmp : -cmp;
    },
  }), []);

  const { sortedData: sortedCompetitors, handleSort: handleCompetitorSort, getSortDirection: getCompetitorSortDirection } = useTableSorting(
    competitors,
    { key: 'competitor_brand_name', direction: 'asc' },
    competitorSortFns
  );

  // Blank to a loader only on the true first load (business query still resolving, or competitors
  // first-loading with no cached data) — and only once it outlasts the delay, so a fast cached nav
  // shows nothing rather than a spinner flash. A background refetch of cached data keeps the list on
  // screen (never re-blanks) (#5342).
  if (isInitialLoading) {
    return showLoader ? (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" className="text-[#290BD9]" />
      </div>
    ) : (
      <div className="h-64" />
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3">
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // Show empty state if no business exists
  if (!business) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-3">
          </div>
        </div>
        <div className="flex-1 space-y-6">
          <TabCardPanel
            tabs={[{ id: 'competitors', label: 'Competitors', isActive: true }]}
          >
            <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
              <Swords className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
              <h3 className="text-lg font-semibold text-gray-500 mb-2">No Competitors</h3>
              <p className="text-gray-500">Competitors will appear here once you create your first campaign</p>
            </div>
          </TabCardPanel>
        </div>
      </div>
    );
  }

  // Check if we should show empty state (no competitors and no tasks in progress)
  const showEmptyState = competitors.length === 0 && !taskStatus?.has_in_progress;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-0.5">
        <div className="flex items-center gap-3">
        </div>
      </div>

      {/* Main Layout: Full width when empty, 9/3 grid when has data */}
      <div className={showEmptyState ? '' : 'grid grid-cols-12 gap-1'}>
        {/* Tab Card - Left Side (9/12 when has data, full width when empty) */}
        <div className={showEmptyState ? '' : 'col-span-9'}>
          {/* Tab Navigation + Action Buttons */}
          <div className="flex items-end justify-between">
            <div className="flex items-end gap-1">
              <button
                className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all cursor-pointer bg-white text-gray-900 border border-gray-200 border-b-transparent"
              >
                Competitors
              </button>
              {!showEmptyState && (
                <button
                  className="relative px-5 py-2.5 text-sm font-medium rounded-t-lg transition-all bg-gray-100 text-gray-700 border border-transparent hover:bg-gray-200 hover:text-gray-900"
                  onClick={() => setIsAddModalOpen(true)}
                >
                  + Add
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 pb-1">
              {showEmptyState && business?.business_url && (
                <Button
                  variant="outline"
                  className="h-9 text-gray-900"
                  disabled={generateMutation.isPending}
                  onClick={() => generateMutation.mutate()}
                >
                  {generateMutation.isPending && <Spinner size="sm" className="mr-2" />}
                  Generate Competitors
                </Button>
              )}
              {!showEmptyState && (
                <>
                  {/* Explicit-save model (#5347/#4719): proximity edits stage locally; this CTA
                      appears only when dirty and persists them. No autosave. */}
                  {(isDirty || isSavingProximity) && (
                    <SaveChangesButton
                      className="h-9 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!isDirty}
                      saving={isSavingProximity}
                      onClick={() => { void saveProximityChanges(); }}
                    />
                  )}
                  <Button
                    variant="outline"
                    className="h-9 text-gray-900"
                    onClick={() => {
                      setPreSelection({ types: [CAMPAIGN_TYPE.COMPETITOR], competitorIds: [], origin: '/competitors' });
                      navigate({ to: '/new/campaign/competitor' });
                    }}
                  >
                    <Megaphone className="h-4 w-4 mr-2" />
                    Create Campaign
                  </Button>
                  <Button
                    variant="outline"
                    className="h-9 text-gray-900"
                    onClick={() => navigate({ to: '/add-to-campaign' })}
                    disabled={!competitors.some((c) => c.campaign)}
                  >
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Add to Campaign
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Card with content */}
          <Card className="rounded-lg rounded-tl-none border bg-white -mt-px">
            <CardContent className="p-0">
              {/* Empty state when no competitors and no tasks in progress */}
              {showEmptyState && (
                <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
                  <Swords className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                  <h3 className="text-lg font-semibold text-gray-500 mb-2">No Competitors Yet</h3>
                  <p className="text-gray-500">Automatically detect competitors based on your business</p>
                </div>
              )}

              {/* Table Content - only render when there are competitors or tasks in progress */}
              {!showEmptyState && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <SortableHeader sortDirection={getCompetitorSortDirection('competitor_brand_name')} onSort={() => handleCompetitorSort('competitor_brand_name')}>
                          Name
                        </SortableHeader>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <SortableHeader sortDirection={getCompetitorSortDirection('competitor_closeness')} onSort={() => handleCompetitorSort('competitor_closeness')}>
                          Proximity
                        </SortableHeader>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Social
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <SortableHeader sortDirection={getCompetitorSortDirection('campaign')} onSort={() => handleCompetitorSort('campaign')}>
                          Campaign
                        </SortableHeader>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Report
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                        {/* Initial competitor detection loading row */}
                        {competitors.length === 0 && taskStatus?.has_in_progress && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8">
                                  <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                                    <Spinner size="sm" className="text-[#290BD9]" />
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-base font-medium text-gray-900">
                                    Researching your competitors
                                  </div>
                                  <div className="text-sm text-gray-500">This can take 8-12 minutes.</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {sortedCompetitors.map((competitor) => (
                          <tr
                            key={competitor.id}
                            className={`hover:bg-gray-50 cursor-pointer ${selectedCompetitorId === competitor.id ? 'bg-[#290BD9]/10' : ''}`}
                            onClick={() => selectCompetitor(competitor.id)}
                          >
                            {competitor.is_loading ? (
                              // Loading state - show spinner and message
                              <td colSpan={6} className="px-6 py-4">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-8 w-8">
                                    <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                                      <Spinner size="sm" className="text-[#290BD9]" />
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-base font-medium text-gray-900">
                                      Researching {competitor.primary_website?.replace(/^https?:\/\//, '') || 'competitor'}
                                    </div>
                                    <div className="text-sm text-gray-500">This can take 2-3 minutes.</div>
                                  </div>
                                </div>
                              </td>
                            ) : (
                              // Completed state - show all data
                              <>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-8 w-8">
                                      <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
                                        <span className="text-xs font-medium text-[#290BD9]">{competitor.competitor_brand_name.substring(0, 2).toUpperCase()}</span>
                                      </div>
                                    </div>
                                    <div className="ml-4 min-w-[200px]">
                                      <div className="text-base font-medium text-gray-900" title={competitor.competitor_brand_name}>
                                        {competitor.competitor_brand_name.length > 36
                                          ? competitor.competitor_brand_name.substring(0, 36) + '…'
                                          : competitor.competitor_brand_name}
                                      </div>
                                      {competitor.primary_website && (
                                        <a
                                          href={competitor.primary_website}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-sm font-normal text-[#290BD9] hover:underline"
                                          title={competitor.primary_website.replace(/^https?:\/\//, '')}
                                        >
                                          {(() => {
                                            const domain = competitor.primary_website.replace(/^https?:\/\//, '');
                                            return domain.length > 36 ? domain.substring(0, 36) + '…' : domain;
                                          })()}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <ProximitySelect
                                    closeness={competitor.competitor_closeness}
                                    onChange={(score) => handleProximityChange(competitor, score)}
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex gap-2">
                                    {competitor.social_media?.linkedin && (
                                      <a
                                        href={competitor.social_media.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-6 w-6 rounded flex items-center justify-center bg-[#0A66C2] hover:bg-[#004182] transition-colors"
                                      >
                                        <Linkedin className="h-3.5 w-3.5 text-white" />
                                      </a>
                                    )}
                                    {competitor.social_media?.twitter && (
                                      <a
                                        href={competitor.social_media.twitter}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-6 w-6 rounded flex items-center justify-center bg-black hover:bg-gray-800 transition-colors"
                                      >
                                        <img src="/x-logo.png" alt="X" className="h-3 w-3 invert" />
                                      </a>
                                    )}
                                    {competitor.social_media?.facebook && (
                                      <a
                                        href={competitor.social_media.facebook}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-6 w-6 rounded flex items-center justify-center bg-[#1877F2] hover:bg-[#0E5FD4] transition-colors"
                                      >
                                        <Facebook className="h-3.5 w-3.5 text-white" />
                                      </a>
                                    )}
                                    {competitor.social_media?.instagram && (
                                      <a
                                        href={competitor.social_media.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-6 w-6 rounded flex items-center justify-center bg-[#E4405F] hover:bg-[#C13584] transition-colors"
                                      >
                                        <Instagram className="h-3.5 w-3.5 text-white" />
                                      </a>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm font-normal text-gray-900 max-xl:max-w-[200px]">
                                  {competitor.campaign ? (
                                    <div className="flex flex-col gap-0.5">
                                      <Link
                                        to="/campaigns/$campaignslug"
                                        params={{ campaignslug: competitor.campaign.slug }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-[#290BD9] hover:underline break-words"
                                      >
                                        {getCampaignShortName(competitor.campaign)}
                                      </Link>
                                      {competitor.campaign.ad && (
                                        <Link
                                          to="/campaigns/$campaignslug"
                                          params={{
                                            campaignslug: competitor.campaign.slug,
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-xs text-gray-500 hover:text-[#290BD9] hover:underline"
                                        >
                                          View Ad
                                        </Link>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-gray-900">
                                  {(() => {
                                    const report = getCompetitorReport(competitor.id);
                                    const generating = isReportGenerating(competitor);

                                    if (report && !generating) {
                                      return (
                                        <Link
                                          to="/reports/$reportId"
                                          params={{ reportId: String(report.id) }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-[#290BD9] hover:underline flex items-center gap-1"
                                        >
                                          <Eye className="h-3.5 w-3.5" />
                                          View
                                        </Link>
                                      );
                                    }

                                    if (generating) {
                                      return (
                                        <span className="text-gray-500 flex items-center gap-1">
                                          <Spinner size="sm" />
                                          Generating...
                                        </span>
                                      );
                                    }

                                    const failed = hasFailedReportTask(competitor);

                                    return (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          requirePayment(async () => {
                                            if (!organizationData?.id) return;
                                            setGeneratingReportIds(prev => new Set(prev).add(competitor.id));

                                            posthog?.capture('report_generation_started', {
                                              report_type: 'COMPETITOR_REPORT',
                                              competitor_id: competitor.id,
                                              competitor_name: competitor.competitor_brand_name,
                                              competitor_url: competitor.primary_website,
                                              organization_id: organizationData.id,
                                            });

                                            try {
                                              await generateCompetitorReport(competitor.id, {
                                                organization_id: organizationData.id,
                                                googleadsaccount_id: googleAdsAccount?.id ? parseInt(googleAdsAccount.id, 10) : undefined,
                                                business_id: business?.id,
                                              });
                                              // Refetch report tasks so isReportGenerating() picks up the new task
                                              await refetchReportTasks();
                                            } catch (err) {
                                              console.error('Error generating report:', err);
                                              posthog?.capture('report_generation_failed', {
                                                report_type: 'COMPETITOR_REPORT',
                                                competitor_id: competitor.id,
                                                competitor_name: competitor.competitor_brand_name,
                                                error: err instanceof Error ? err.message : 'Unknown error',
                                                organization_id: organizationData.id,
                                              });
                                              setGeneratingReportIds(prev => {
                                                const next = new Set(prev);
                                                next.delete(competitor.id);
                                                return next;
                                              });
                                            }
                                          });
                                        }}
                                        disabled={generatingReportIds.has(competitor.id)}
                                        className={`hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 ${failed ? 'text-red-600' : 'text-[#290BD9]'}`}
                                      >
                                        {generatingReportIds.has(competitor.id) ? (
                                          <>
                                            <Spinner size="sm" />
                                            Generating...
                                          </>
                                        ) : failed ? (
                                          <>
                                            <AlertCircle className="h-3.5 w-3.5" />
                                            Failed - Retry
                                          </>
                                        ) : (
                                          <>
                                            <FilePlus className="h-3.5 w-3.5" />
                                            Generate
                                          </>
                                        )}
                                      </button>
                                    );
                                  })()}
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setCompetitorToDelete(competitor); }}
                                    className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Remove competitor"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Details Panel - Right Side (3/12) - only show when has data */}
        {!showEmptyState && (
        <div className="col-span-3">
          <div className="sticky top-0 mt-[42px]">
            {/* Show details when competitor selected */}
              {selectedCompetitorId && (() => {
                const competitor = competitors.find(c => c.id === selectedCompetitorId);
                if (!competitor) return null;

                return (
                  <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4 space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900 break-words">{competitor.competitor_brand_name}</h3>

                    <div className="space-y-3">
                      {competitor.competitor_metadata?.industry && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Industry Focus</div>
                          <div className="text-gray-900">{competitor.competitor_metadata.industry}</div>
                        </div>
                      )}

                      {competitor.competitor_metadata?.audience && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Target Audience</div>
                          <div className="text-gray-900">{competitor.competitor_metadata.audience}</div>
                        </div>
                      )}

                      {competitor.description && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Description</div>
                          <div className="text-gray-900">{competitor.description}</div>
                        </div>
                      )}

                      {/* Competitor Proximity */}
                      <div>
                        <div className="text-xs text-gray-700 mb-1">Competitor Proximity</div>
                        <ProximitySelect
                          closeness={competitor.competitor_closeness}
                          onChange={(score) => handleProximityChange(competitor, score)}
                        />
                      </div>

                      {/* Ad Libraries */}
                      {(competitor.primary_website || competitor.competitor_brand_name) && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Ad Libraries</div>
                          <div className="flex gap-2">
                            {competitor.primary_website && (
                              <a
                                href={`https://adstransparency.google.com/?region=anywhere&domain=${encodeURIComponent(competitor.primary_website.replace(/^https?:\/\//, '').replace(/\/.*$/, ''))}&format=TEXT&preset-date=Last+30+days`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-6 w-6 flex-shrink-0"
                                title="View Google Ads"
                              >
                                <img src="/google-ads.svg" className="h-6 w-6" alt="Google Ads" />
                              </a>
                            )}
                            {competitor.competitor_brand_name && (
                              <a
                                href={`https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=ALL&q=${encodeURIComponent(competitor.competitor_brand_name)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-6 w-6 flex-shrink-0 rounded flex items-center justify-center bg-[#1877F2] hover:bg-[#0E5FD4] transition-colors"
                                title="View Meta Ads"
                              >
                                <Facebook className="h-3.5 w-3.5 text-white" />
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {competitor.location && (competitor.location.town || competitor.location.region || competitor.location.country) && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Location</div>
                          <div className="text-gray-900">
                            {[competitor.location.town, competitor.location.region, competitor.location.country]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        </div>
                      )}

                      {competitor.competitor_metadata?.founding_year && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Founded</div>
                          <div className="text-gray-900">{competitor.competitor_metadata.founding_year}</div>
                        </div>
                      )}

                      {competitor.competitor_metadata?.founders && competitor.competitor_metadata.founders.length > 0 && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Founders</div>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {competitor.competitor_metadata.founders.map((founder, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-900">
                                {founder}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Empty State */}
              {!selectedCompetitorId && (
                <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                  <Users className="mx-auto h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
                  <h3 className="text-lg font-semibold text-gray-500 mb-2">Select a Competitor</h3>
                  <p className="text-gray-500">
                    Click any row in the table to view detailed competitor analysis
                  </p>
                </div>
            )}
          </div>
        </div>
        )}
      </div>

      <AddCompetitorModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        businessId={business.id}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['competitors', business.id] });
        }}
      />

      {/* Payment Gate Modal */}
      <PaymentGate />

      {/* Remove Competitor Confirmation Modal */}
      <ConformationModal
        isOpen={competitorToDelete !== null}
        onClose={() => setCompetitorToDelete(null)}
        onConfirm={() => {
          if (competitorToDelete) {
            deleteMutation.mutate(competitorToDelete.id);
          }
        }}
        title="Remove Competitor"
        description={
          competitorToDelete?.campaign
            ? `Are you sure you want to remove ${competitorToDelete.competitor_brand_name}? This competitor will be removed from all campaigns.`
            : `Are you sure you want to remove ${competitorToDelete?.competitor_brand_name ?? ''}?`
        }
        confirmText="Remove"
        isLoading={deleteMutation.isPending}
      />

      {/* #4792: warn before navigating away with unsaved proximity edits */}
      {UnsavedChangesModal}
    </div>
  );
}
